import React from "react";
import clsx from "clsx";
import i18n from "i18n";
import {
  Grid,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  withStyles,
} from "@material-ui/core";
import { SubdirectoryArrowRight } from "@material-ui/icons";
import { WBSPropertiesStore } from "modules/WBS/WBSPropertiesStore";
import { formatMonetary, formatNumber } from "utils/MathUtils";
import { roundBy, times } from "utils/DecimalUtils";
import { ITableOccurencesRow } from "common/interfaces";

const descriptionWidth = 32; // %

const styles = (theme) => ({
  fixedTableHead: {
    overflowY: "auto" as any,
    height: 281.5,

    "& table": {
      borderCollapse: "collapse" as any,
    },
    "& thead th": {
      position: "sticky" as any,
      top: 0,
    },
  },
  cellWrapper: {
    padding: "1rem 0.75rem",
    background: "white",
    cursor: "default",
    transition: "all 0.3s ease-in",

    "&:hover": {
      backgroundColor: "white",
    },

    "& > a": {
      color: "#137CBD",
      fontWeight: 500,
      cursor: "pointer",
    },
    "&.column-description > p": {
      display: "inline-block",
      width: descriptionWidth * 10,
      whiteSpace: "nowrap" as any,
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    "& .icon": {
      fontSize: "1rem",
      display: "inline-block",
      color: "#424242",
    },
  },
  cellHighlighted: {
    "& > p": {
      fontWeight: 600,
    },
  },
});

const CustomCell = ({
  className,
  label = "",
  width = "auto",
  align = "left",
  indent = 1,
  children,
}: {
  className?: string;
  label?: string;
  width?: string;
  align?: string;
  indent?: number;
  children?: any;
}) => {
  return (
    <TableCell
      className={className}
      align={align as any}
      style={{
        width,
        paddingLeft: `${indent}rem`,
      }}
    >
      {children ? children : <Typography>{label}</Typography>}
    </TableCell>
  );
};

interface IProps {
  classes: any;
  rows: ITableOccurencesRow[];
  onClickLink: (id: string) => void;
}

/** Componente tabela da janela de exibir ocorrências de um item da EAP. */
const TableOccurences = ({ classes, rows, onClickLink }: IProps) => {
  const columns = [
    { key: "treeCode", width: "10%" },
    { key: "description", width: `${descriptionWidth}%` },
    { key: "quantity", width: "8%" },
    { key: "unit", width: "8%" },
    { key: "unitCost", width: "16%" },
    { key: "total", width: "18%" },
    { key: "percentual", width: "8%", align: "center" },
  ];

  return (
    <Grid className={classes.fixedTableHead}>
      <Table data-testid="table-ItemOccurrences">
        <TableHead>
          {columns.map((col) => (
            <CustomCell
              key={"header_" + col.key}
              className={classes.cellWrapper}
              label={i18n.wbs.itemOccurrences.fields[col.key]}
              width={col.width}
              align={col.align}
            />
          ))}
        </TableHead>

        <TableBody>
          {rows.map((row) => (
            <TableRow>
              <CustomCell
                className={clsx(classes.cellWrapper, {
                  [classes.cellHighlighted]: !row.isParent,
                })}
              >
                <Link underline="always" onClick={() => onClickLink(row.id)}>
                  {row.treeCode}
                </Link>
              </CustomCell>
              <CustomCell
                className={clsx(classes.cellWrapper, "column-description", {
                  [classes.cellHighlighted]: !row.isParent,
                })}
                indent={row.indent}
              >
                <Tooltip title={row.description}>
                  <Typography>
                    {row.indent && row.indent > 1 && (
                      <SubdirectoryArrowRight
                        className="icon"
                        fontSize="small"
                      />
                    )}
                    {row.description}
                  </Typography>
                </Tooltip>
              </CustomCell>
              <CustomCell
                className={clsx(classes.cellWrapper, {
                  [classes.cellHighlighted]: !row.isParent,
                })}
                label={formatNumber(
                  roundBy(row.quantity, WBSPropertiesStore.getRules(10)),
                  0,
                  10
                )}
              />
              <CustomCell
                className={clsx(classes.cellWrapper, {
                  [classes.cellHighlighted]: !row.isParent,
                })}
                label={row.unit}
                align="center"
              />
              <CustomCell
                className={clsx(classes.cellWrapper, {
                  [classes.cellHighlighted]: !row.isParent,
                })}
                label={formatMonetary(
                  roundBy(row.unitCost, WBSPropertiesStore.getRules(2))
                )}
                align="right"
              />
              <CustomCell
                className={clsx(classes.cellWrapper, {
                  [classes.cellHighlighted]: !row.isParent,
                })}
                label={formatMonetary(
                  roundBy(row.total, WBSPropertiesStore.getRules(2))
                )}
                align="right"
              />
              <CustomCell
                className={clsx(classes.cellWrapper, {
                  [classes.cellHighlighted]: !row.isParent,
                })}
                label={
                  row.isParent
                    ? ""
                    : `${formatNumber(
                        roundBy(row.percentual, WBSPropertiesStore.getRules(2))
                      )}%`
                }
                align="center"
              />
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Grid>
  );
};

export default withStyles(styles)(TableOccurences);



// WEBPACK FOOTER //
// ./src/modules/WBS/components/layouts/DialogItemOccurrences/TableOccurences.tsx